<template>
  <div :class="$style['datepicker']">
    <VueDatePicker
      v-model="localValue"
      ref="dpRef"
      locale="ru"
      :enable-time-picker="props.enableTimePicker"
      :model-type="convertTokensToUnicodeFormat(props.valueType)"
      :format="convertTokensToUnicodeFormat(props.format)"
      :placeholder="props.placeholder"
      :name="props.name"
      :text-input="{
        format: convertTokensToUnicodeFormat(props.format),
      }"
      position="left"
      hide-offset-dates
      :ui="{
        menu: `${$style['datepicker__menu']} ${$style['datepicker_vars']} ${$style[props.dark ? 'datepicker_vars-dark' : 'datepicker_vars-light']} ${props.popupClass}`,
        input: `${$style['datepicker__input']} ${$style['datepicker_vars']} ${$style[props.dark ? 'datepicker_vars-dark' : 'datepicker_vars-light']} ${props.inputClass}`,
        calendar: `${$style['datepicker__calendar']}`,
      }"
      :clearable="props.clearable"
      teleport="#app"
      :disabled-dates="props.disabledDate"
      :disabled="props.disabled"
      :hide-navigation="['month', 'year']"
      :id="props.id"
      :month-picker="!convertTokensToUnicodeFormat(props.format).includes('d')"
      :range="props.range"
      :min-time="props.minTime"
      :max-time="props.maxTime"
      :start-time="props.startTime"
      @text-input="inputValue = $event.target.value"
      @blur="onBlur"
      @focus="onFocus"
      @date-update="onDateClick"
      @time-picker-close="onTimeClose"
      @range-end="onDateRangeEndClick"
    >
      <template #input-icon >
        <div
          class="icon icon_fix icon_size-16"
          :class="{
            ['icon-calendar']: !dark,
            ['icon-calendar-white']: dark,
          }"
        />
      </template>
      <template #clear-icon>
        <DIcon :name="EIcon.CHECK_CLOSE" bold :size="EIconSize.S_16" :color="props.dark ? EIconColor.WHITE_101 : EIconColor.GREY_1514"/>
      </template>
      <template #arrow-left>
        <DIcon :name="EIcon.ARROW_LEFT" bold :size="EIconSize.S_16" :color="props.dark ? EIconColor.WHITE_101 : EIconColor.GREY_1514"/>
      </template>
      <template #arrow-right>
        <DIcon :name="EIcon.ARROW_RIGHT" bold :size="EIconSize.S_16" :color="props.dark ? EIconColor.WHITE_101 : EIconColor.GREY_1514"/>
      </template>
      <template #action-buttons>
        <button :class="$style['datepicker__reset']" @click="onReset">Сбросить</button>
        <button :class="$style['datepicker__apply']" @click="onApply">Применить</button>
      </template>
    </VueDatePicker>
  </div>
</template>
<script setup>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { watch, nextTick, inject } from 'vue';
import { DIcon, EIcon, ESize as EIconSize, EColor as EIconColor } from '@ahml/domrfbank-ui/elements/DIcon.js';
import { ref } from 'vue';
import dayjs from 'dayjs';

defineOptions({
  compatConfig: {
    MODE: 3,
  },
});

const props = defineProps({
  modelValue: [String, Array],
  format: {
    type: String,
    default: 'dd.MM.yyyy',
  },
  valueType: {
    type: String,
    default: 'dd.MM.yyyy',
  },
  placeholder: {
    type: String,
    default: 'ДД.ММ.ГГГГ',
  },
  name: String,
  inputClass: String,
  popupClass: String,
  clearable: Boolean,
  disabledDate: [Array, Function],
  disabled: Boolean,
  dark: Boolean,
  id: String,
  range: Boolean,
  enableTimePicker: Boolean,
  minTime: {
    type: Object,
    default: undefined,
  },
  maxTime: {
    type: Object,
    default: undefined,
  },
  startTime: {
    type: Object,
    default: undefined,
  },
});

const emit = defineEmits([
  'update:model-value',
  'blur',
  'focus',
]);

const convertTokensToUnicodeFormat = (tokens) => {
  return tokens.replaceAll('D', 'd').replaceAll('Y', 'y');
};

const convertTokensToDayjsFormat = (tokens) => {
  const res = tokens.replaceAll('d', 'D').replaceAll('y', 'Y');
  return res;
};

const localValue = ref(props.modelValue);

watch(() => props.modelValue, (val) => {
  localValue.value = val;
  
  const valD = dayjs(val, convertTokensToDayjsFormat(props.valueType));
  inputValue.value = valD.format(convertTokensToDayjsFormat(props.format));
});

watch(localValue, (val) => {
  emit('update:model-value', val);
});

const dpRef = ref(null);

const onReset = () => {
  dpRef.value.clearValue();
  dpRef.value.closeMenu();
};

const onApply = () => {
  dpRef.value.selectDate();
};

const inputValue = ref('');

const setIsAutoRecognizeFocus = inject('setIsAutoRecognizeFocus') ?? (() => {});
const setIsFocused = inject('setIsFocused') ?? (() => {});

setIsAutoRecognizeFocus(false);

const onBlur = async (e) => {
  await nextTick();
  
  const valD = dayjs(inputValue.value, convertTokensToDayjsFormat(props.format));

  if (valD.isValid()) {
    const isDisabledDate = typeof props.disabledDate === 'function' && props.disabledDate(valD.toDate());
    const isDisabledTime = props.enableTimePicker
      && (
        (props.minTime?.hours && valD.hour() < props.minTime.hours)
        || (props.maxTime?.hours && valD.hour() > props.maxTime.hours)
      );
    const isDisabled = isDisabledDate || isDisabledTime;

    if (isDisabled) return;
    
    localValue.value = valD.format(convertTokensToDayjsFormat(props.valueType));
  }

  setIsFocused(false);
  emit('blur', e);
};

const onFocus = () => {
  setIsFocused(true);
  emit('focus');
};

const onDateClick = async () => {
  if (props.range) return;
  if (props.enableTimePicker) {
    dpRef.value.switchView('time');
    return;
  }
  await nextTick();
  onApply();
};

const onDateRangeEndClick = async () => {
  await nextTick();
  onApply();
};

const onTimeClose = (v) => {
  console.log(v)
};

</script>
<style lang="scss" module>
@import '../../assets/scss/base/_variables.scss';
@import '../../assets/scss/base/_functions.scss';
@import '../../assets/scss/base/_mixins.scss';

.datepicker {
  & :global(.dp__input_icon) {
    left: unset;
    line-height: 1;
    @include fluid('right', 16px);
  }

  &_vars {
    @include fluid('--dp-border-radius', 16px);
    @include fluid('--dp-font-size', 14px);
    --dp-action-row-padding: 0px;
    @include fluid('--dp-menu-min-width', 304px);
    @include fluid('--dp-menu-padding', 16px);
    @include fluid('--dp-cell-size', 32px);
    @include fluid('--dp-cell-padding', 6px);
  }

  &_vars-light {
    --dp-border-color: #{$grey-166};
    --dp-icon-color: #{$grey-1514};
    --dp-hover-icon-color: #{$grey-1514};
    --dp-primary-color: #{$green-223};
    --dp-text-color: #{$grey-1514};
    --dp-background-color: #{$white-101};
    --dp-hover-color: #{$grey-114};
  }

  &_vars-dark {
    --dp-border-color: #{$grey-517};
    --dp-icon-color: #{$white-101};
    --dp-hover-icon-color: #{$grey-868};
    --dp-primary-color: #{$green-223};
    --dp-text-color: #{$white-101};
    --dp-background-color: #{$grey-868};
    --dp-hover-color: #{$grey-114};
  }

  &__menu {
    & :global(.dp__arrow_top), & :global(.dp__arrow_bottom) {
      display: none;
    }

    & :global(.dp__calendar_header) {
      @include p3;
      font-weight: 400;
      text-transform: uppercase;
      color: $grey-284;
    }

    & :global(.dp__month_year_wrap) {
      font-weight: 500;
    }

    & :global(.dp__today) {
      color: $green-223;
      border: none;
    }

    & :global(.dp__range_end),
    & :global(.dp__range_start),
    & :global(.dp__active_date) {
      color: $white-101;
      background: $green-223;
      @include fluid('border-radius', 8px);
    }

    & :global(.dp__calendar_item) {
      position: relative;
    }

    & :global(.dp__calendar_item):first-child {
      position: relative;
    }

    & :global(.dp__calendar_item:has(.dp__range_start))::before,
    & :global(.dp__calendar_item:has(.dp__range_end))::before,
    & :global(.dp__calendar_item:has(.dp__range_between))::before {
      display: block;
      content: "";
      position: absolute;
      top: 0px;
      left: 0px;
      width: 100%;
      @include fluid('height', 32px);
      background: var(--dp-hover-color);
    }

    & :global(.dp__calendar_item:has(.dp__range_end))::before {
      @include fluid('width', 32px);
      border-top-right-radius: var(--dp-border-radius);
      border-bottom-right-radius: var(--dp-border-radius);
    }

    & :global(.dp__calendar_item:has(.dp__range_start))::before {
      border-top-left-radius: var(--dp-border-radius);
      border-bottom-left-radius: var(--dp-border-radius);
    }

    & :global(.dp__action_buttons) {
      flex: 1;
    }

    & :global(.dp__selection_preview) {
      display: none;
    }

    & :global(.dp__overlay_cell_active) {
      color: $green-223;
      background-color: transparent;
    }

    & :global(.dp__calendar_header_separator) {
      display: none;
    }
  }

  &__input {
    font-size: 1rem;
  }

  &__calendar {
    font-weight: 500;
  }

  &__reset, &__apply {
    flex: 1;
    @include p1;
    font-weight: 500;
    @include fluid('padding-top', 16px);
    @include fluid('padding-bottom', 16px);
    border-top: 1px solid $grey-134;
    cursor: pointer;

    &:not(:last-child) {
      border-right: 1px solid $grey-134;
    }
  }
}
</style>